/* ============================================================== */
/*                                                                */
/*   errorTextV4  ·  แปลง error จาก issara-sdk tuple เป็นข้อความ      */
/*                                                                */
/*   ไฟล์นี้ต้อง import อะไรไม่ได้เลย (pure) เพราะ domain model      */
/*   (patient/examModelV4.ts) ใช้มัน และ test แบบ Node ไม่ควรลาก      */
/*   CommonInterface/react-dom เข้ามาใน dependency graph             */
/*                                                                */
/* ============================================================== */

/** SDK บาง endpoint ส่ง error เป็น ArrayBuffer/Uint8Array */
const bytesLike = (value: unknown): boolean =>
  value instanceof ArrayBuffer || (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(value));

const bytesToText = (value: unknown): string | null => {
  if (typeof value === "string") return value;
  if (!bytesLike(value)) return null;

  const view = value instanceof ArrayBuffer ? new Uint8Array(value) : (value as Uint8Array);

  try {
    if (typeof TextDecoder !== "undefined") return new TextDecoder("utf8").decode(view);
  } catch (decoderError) {
    /* jsdom เวอร์ชันเก่าใน jest ของ repo ไม่มี TextDecoder */
  }

  try {
    const latin = String.fromCharCode.apply(null, Array.from(view) as number[]);
    return decodeURIComponent(escape(latin));
  } catch (fallbackError) {
    return null;
  }
};

/* ============================================================== */
/*   SECTION 2 · tuple จริงของ issara-sdk (BaseService + to)          */
/* ============================================================== */

/**
 * !! tuple ของ issara-sdk ฉบับ BaseService (`react-lib/apis/services/BaseService.ts`)
 *
 * utils.to(promise) คืน [error, data, extra] แล้วไฟล์ sdk ที่ generate ขึ้นคืน
 *   [result[1], result[0], result[2]]  =>  [responseData, errorBody, axiosResult]
 *
 *   สำเร็จ  ->  [ {items:[...]},  null,  axios response object (truthy!) ]
 *   ล้มเหลว ->  [ null,  body 4xx/5xx,  axios error (isAxiosError:true) ]
 *
 * ดังนั้นช่องที่ 3 "ไม่ ใช่ boolean network" — ถ้าเผลอตรวจว่า truthy
 * จะขึ้น "เชื่อมต่อเซิร์ฟเวอร์ไม่ได้" ทุกครั้งแม้ API สำเร็จ (เคยเกิดจริงกับหน้าคิว)
 * ตรวจความล้มเหลวต้องดู isAxiosError / request-ไม่มี-response เท่านั้น
 */

/** value เป็น axios error (ล้มเหลว) หรือ axios response (สำเร็จ) */
export const isAxiosFailure = (value: unknown): boolean => {
  if (value instanceof Error) return true;
  if (value === null || typeof value !== "object") return false;

  const record = value as { isAxiosError?: unknown; request?: unknown; status?: unknown };
  if (record.isAxiosError === true) return true;
  /* axios error ที่ BaseService ยังไม่เติม response ให้ (network จริง ๆ) */
  if (record.request !== undefined && record.status === undefined) return true;
  return false;
};

const axiosErrorText = (value: unknown, fallbackText: string): string | null => {
  const error = value as { response?: { status?: unknown; data?: unknown }; message?: unknown };
  const status = typeof error?.response?.status === "number" ? error.response.status : undefined;

  /* ไม่มี response = request ออกจากเครื่องแล้วเซิร์ฟเวอร์ไม่ตอบ (timeout / DNS / CORS) */
  if (status === undefined) return fallbackText;

  return errorTextOf(error?.response?.data) ?? `เกิดข้อผิดพลาดจาก API (${status})`;
};

/**
 * อ่าน error จาก tuple ของ issara-sdk อย่างปลอดภัย
 * @param requireData endpoint ที่ต้องมีข้อมูลกลับมา (เช่น list คิว) ถ้า data = null โดยไม่มี error body = ถือว่าล้มเหลว
 */
export const apiErrorTextOf = (
  result: [unknown, unknown, unknown] | null | undefined,
  options: { fallbackText?: string; requireData?: boolean } = {},
): string | null => {
  const { fallbackText = "เชื่อมต่อเซิร์ฟเวอร์ไม่ได้", requireData = false } = options;
  const [data, errorBody, axiosResult] = result ?? [];

  const bodyText = errorTextOf(errorBody);
  if (bodyText !== null) return bodyText;

  /* {responseData, error} จาก BaseService.throwErrorMessage เช่น backend 500 ที่ตอบเป็น HTML traceback
     ผู้ใช ได้ข้อความสั้นพร้อม HTTP code ไม่ได้ HTML ทั้งหน้า */
  const envelope = [data, errorBody, axiosResult].find(
    (item) => typeof item === "object" && item !== null && (item as { responseData?: unknown }).responseData !== undefined,
  );
  if (envelope !== undefined) {
    const status = (envelope as { error?: { response?: { status?: unknown } }; status?: unknown }).error?.response?.status;
    const code = typeof status === "number" ? status : (envelope as { status?: unknown }).status;
    return typeof code === "number" ? `เซิร์ฟเวอร์ตอบผิด (HTTP ${code})` : fallbackText;
  }

  /* axios error อยู่ช่องไหนก็ได้ขึ้นกับ shape ที่ to() คืน (บาง path คืน [error] ช่องเดียว) */
  const failure = [axiosResult, errorBody, data].find(isAxiosFailure);
  if (failure !== undefined) return axiosErrorText(failure, fallbackText);

  if (requireData && (data === null || data === undefined)) return fallbackText;
  return null;
};

/** Django (DEBUG=True) ตอบ HTML traceback กลับมาทั้งหน้า — ห้ามเอาไปแสดงใน UI เด็ดขาด */
const looksLikeHtml = (text: string): boolean => {
  const head = text.replace(/^[\s\ufeff]+/, "").slice(0, 400).toLowerCase();
  return head.startsWith("<") && ["<!doctype html", "<html", "<body", "<pre"].some((mark) => head.includes(mark));
};

/** แถบ error ของ card มีที่นิดเดียว ข้อความยาวเกินต้องตัด (backend บางตัวตอบ message ยาวเป็นพันตัวอักษร) */
export const ERROR_TEXT_MAX = 300;
const clip = (text: string): string => (text.length <= ERROR_TEXT_MAX ? text : `${text.slice(0, ERROR_TEXT_MAX).trimEnd()}…`);

/** axios internals (config/request/response) are never shown to the user */
const isAxiosObject = (value: unknown): boolean => {
  const record = value as { isAxiosError?: unknown; config?: unknown; response?: unknown; request?: unknown };
  return record.isAxiosError === true || (record.config !== undefined && record.response !== undefined);
};

/**
 * แปลง error ที่มาจาก issara-sdk tuple [data, error, network] เป็นข้อความที่แสดงได้
 * รองรับ string, ArrayBuffer/Uint8Array (JSON ของ DRF), DRF dict {detail|non_field_errors|field:[...]}
 * body ที่เป็น HTML หรือ axios internals คืน null ให้ caller ใช้ fallback ของมันเอง
 */
export const errorTextOf = (error: unknown): string | null => {
  if (error === null || error === undefined) return null;

  const text = bytesToText(error);
  if (text !== null) {
    const trimmed = text.trim();
    if (trimmed === "" || looksLikeHtml(trimmed)) return null;
    try {
      const inner = errorTextOf(JSON.parse(trimmed));
      return inner === null ? null : clip(inner);
    } catch (parseError) {
      return clip(trimmed);
    }
  }

  if (Array.isArray(error)) {
    const texts = error.map(errorTextOf).filter((text): text is string => text !== null);
    return texts.length > 0 ? texts.join(" · ") : null;
  }

  if (typeof error === "object") {
    const record = error as { [key: string]: unknown };

    /* BaseService.throwErrorMessage throw {responseData, error} — อ่าน body ของ backend ก่อน */
    if (record.responseData !== undefined) {
      const inner = errorTextOf(record.responseData);
      if (inner !== null) return inner;
    }

    const single = record.detail ?? record.message;
    if (typeof single === "string" && single !== "") return single;

    const nonField = record.non_field_errors;
    if (Array.isArray(nonField) && nonField.length > 0) return nonField.map((item) => `${item}`).join(", ");

    /* DRF custom error ที่ไม่ใช่ array/string เช่น {"action":{"code":"ACTION_NOT_ALLOWED","message":"ไม่สามารถ ..."}} */
    const nested = Object.entries(record)
      .filter(([, value]) => value !== null && typeof value === "object" && !Array.isArray(value) && !isAxiosObject(value))
      .map(([key, value]) => {
        const inner = value as { message?: unknown; detail?: unknown; code?: unknown };
        const text =
          typeof inner.message === "string"
            ? inner.message
            : typeof inner.detail === "string"
            ? inner.detail
            : typeof inner.code === "string"
            ? inner.code
            : null;
        return text === null ? null : `${key}: ${text}`;
      })
      .filter((item): item is string => item !== null);
    if (nested.length > 0) return nested.join(" · ");

    const parts = Object.entries(record)
      .filter(([key, value]) => key !== "responseData")
      .filter(([, value]) => (Array.isArray(value) || typeof value === "string") && !(typeof value === "string" && looksLikeHtml(value)))
      .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.map((item) => `${item}`).join(", ") : value}`);
    if (parts.length > 0) return clip(parts.join(" · "));
  }

  return null;
};

